import { Alert }                  from '@chakra-ui/react';
import { Box }                    from '@chakra-ui/react';
import { Button }                 from '@/components/ui/button';
import GeneralInformationSection  from './GeneralInformationSection';
import ImagesSection              from './ImagesSection';
import React                      from 'react';
import SendToValidationModal      from '../modals/SendToValidationModal';
import Spinner                    from '@/components/spinner/Spinner';
import SocialNetworksSection      from './SocialNetworksSection';
import { useEditStandForm }       from '../hooks/useEditStandForm';
import { useTranslation }         from 'react-i18next';
import { EditStandPageData }      from '@/pages/Stand/EditStandPage';

interface IEditStandFormProps {
	data: EditStandPageData;
	isDeadlinePassed?: boolean;
}

const EditStandForm = ({ data, isDeadlinePassed = false }: IEditStandFormProps) => {
	const { t } = useTranslation();
	const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);

	const {
		linkTypes,
		isLoading,
		isFormDisabled,
		willCreateNewStandPage,
		editors,
		control,
		register,
		watch,
		setValue,
		formState: { isSubmitting, errors },
		socialNetworksField,
		standTitle,
		onSubmit,
		onDraft,
	} = useEditStandForm(data);

	const logo                = watch('logo');
	const gallery             = watch('gallery');
	const existingLogoUrl     = watch('existingLogoUrl');
	const existingGalleryUrls = watch('existingGalleryUrls');
	const { hall, slot } = data.stand;
	const { corporateName } = data;

	const isReadOnly = isFormDisabled || isDeadlinePassed;

	if (isLoading || !linkTypes) {
		return <Spinner />;
	}

	return (
		<>
			{willCreateNewStandPage && !isDeadlinePassed && (
				<Alert.Root status="info" mb="10px" boxShadow="sm">
					<Alert.Indicator />
					<Alert.Title lineHeight="1.4">
						{t('pages.editStand.form.newStandPageBanner')}
					</Alert.Title>
				</Alert.Root>
			)}

			{!isDeadlinePassed && (!willCreateNewStandPage || isFormDisabled) && (
				<Alert.Root status="warning" mb="10px" boxShadow="sm">
					<Alert.Indicator />
					<Alert.Title lineHeight="1.4">
						{t(isFormDisabled ? 'pages.editStand.form.validationPendingBanner' : 'pages.editStand.form.validationBanner')}
					</Alert.Title>
				</Alert.Root>
			)}

			<Box
				display="flex"
				flexDirection="column"
				backgroundColor="white"
				borderRadius="md"
				lineHeight="0px"
				p="30px"
				shadow="sm"
			>
				<GeneralInformationSection
					editors={editors}
					standTitle={standTitle}
					corporateName={corporateName}
					isDisabled={isReadOnly}
					hall={hall}
					slot={slot}
				/>

				<SocialNetworksSection
					control={control}
					register={register}
					errors={errors}
					fields={socialNetworksField.fields}
					linkTypes={linkTypes}
					onAdd={() => socialNetworksField.append({ linkTypeUrn: '', url: '' })}
					onRemove={socialNetworksField.remove}
					isDisabled={isReadOnly}
				/>

				<ImagesSection
					logo={logo}
					gallery={gallery}
					existingLogoUrl={existingLogoUrl}
					existingGalleryUrls={existingGalleryUrls}
					onLogoChange={file => setValue('logo', file)}
					onGalleryChange={files => setValue('gallery', files)}
					onExistingLogoDelete={() => setValue('existingLogoUrl', null)}
					onExistingGalleryDelete={index => setValue('existingGalleryUrls', existingGalleryUrls.filter((_, i) => i !== index))}
					isDisabled={isReadOnly}
				/>

				<Box display="flex" justifyContent="flex-end" gap="10px" mt="30px">
					<Button
						variant="outline"
						type="button"
						loading={isSubmitting}
						disabled={isReadOnly || isSubmitting}
						onClick={onDraft}
					>
						{t('pages.editStand.form.actions.saveDraft')}
					</Button>
					<Button
						bg="black"
						color="white"
						type="button"
						_hover={{ bg: 'gray.800' }}
						disabled={isReadOnly || isSubmitting}
						onClick={() => setConfirmModalOpen(true)}
					>
						{t('pages.editStand.form.actions.saveAndSendToValidation')}
					</Button>
				</Box>
			</Box>

			<SendToValidationModal
				isOpen={confirmModalOpen}
				onClose={() => setConfirmModalOpen(false)}
				onConfirm={async () => {
					await onSubmit();
					setConfirmModalOpen(false);
				}}
			/>
		</>
	);
};

export default EditStandForm;
